import { useEffect, useMemo, useState } from "react";

import { completeToken, redirectToCSAT, redirectToMobi, saveEventHistory } from "../services/legacyService";
import { ConnectionStatus, EventType, SessionStatus, Signals } from "../shared/enums";
// import { uploadCurrentScreen } from "../providers/Storage";
// import { FirebaseProvider } from "../providers/Firebase";
// import { OracleStorageProvider } from "../providers/Oracle";

import { Publisher, Session, SignalEvent, StreamEvent, StreamManager, StreamManagerEvent } from "openvidu-browser-v2compatibility";
import { PAR_URL, CAPTURE_VIA_MOBI } from "../shared/constants";
import { capturePhotoBlob } from "../shared/capturePhoto";
import { uploadCaptura, classificarCaptura } from "../services/mobiService";

export default function useRTCEvents(props: {
  connectionStatus: ConnectionStatus;
  captureCurrentScreen: any;
  setIsAccessDenied: any;
  insuranceId?: string;
  userId?: string;
}) {
  const [subscribers, setSubscribers] = useState<StreamManager[]>([]);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>(SessionStatus.Awaiting);
  // const firebaseProvider = useMemo(() => new FirebaseProvider(), []);
  // const oracleProvider = useMemo(() => new OracleStorageProvider('staging-televistoria-images'), []);

  function subscribeToPublisherEvents(session: Session, publisher: Publisher) {
    // TODO: refresh publisher
    publisher.on("streamPlaying", (event: StreamManagerEvent) => {
      console.log(`publisher.streamPlaying`, event);
      saveEventHistory(`Segurado: Transmissão de áudio e vídeo estabelecida`, session.sessionId);
    });

    publisher.on("streamDestroyed", (event: StreamEvent) => {
      saveEventHistory(`Segurado: Transmissão de áudio e vídeo interrompida`, session.sessionId);
    });

    publisher.on("accessAllowed", () => {
      // props.setIsAccessAllowed(publisher.accessAllowed);
      console.warn(`[accessAllowed] ${session.sessionId}`);
      saveEventHistory(`Segurado: Acessos à câmera e microfone concedidos`, session.sessionId);
    });

    publisher.on("accessDenied", () => {
      props.setIsAccessDenied(true);
      console.warn(`[accessDenied] ${session.sessionId}`);

      saveEventHistory(`Segurado: Acessos à câmera e microfone não concedidos`, session.sessionId);
    });
  }

  async function uploadCurrentScreen(payload: any, blob: Blob) {

    if (!blob) {
      throw new Error('No file selected');
    }

    const xhr = new XMLHttpRequest();

    // console.log(`${PAR_URL}${payload.sessionId}/${payload.uniqueId}.png`);
    xhr.open('PUT', `${PAR_URL}${payload.sessionId}/${payload.uniqueId}.jpg`, true);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100;
        console.log(`Upload progress: ${percentComplete}%`);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        console.log('File uploaded successfully');
        return;
      } else {
        console.error('Upload failed', xhr.statusText);
        throw new Error(`Upload failed: ${xhr.statusText}`);
      }
    };

    xhr.onerror = () => {
      console.error('Upload error', xhr.statusText);
      throw new Error('Upload error');
    };

    xhr.send(blob);
  }

  // TV-2 — captura via Mobi.Core (CAPTURE_VIA_MOBI=1): foto do SENSOR (ImageCapture;
  // fallback frame do stream em JPEG no iOS) + POST /capturas (linha completa: hash,
  // antifraude, histórico, OCI, thumb) + classificação por IA. Substitui PUT na PAR →
  // serviço de mídia → apiToken.cfm?act=saveFotoDB. O sinal signedURL que o perito já
  // escuta (e que dispara o loadGallery na página-mãe) passa a ser emitido daqui.
  async function captureViaMobi(publisher: Publisher, event: SignalEvent) {
    const sessionId = publisher.session.sessionId;
    try {
      const track = publisher.stream?.getMediaStream()?.getVideoTracks()[0];
      const { video } = publisher.videos[publisher.videos.length - 1];
      const blob = await capturePhotoBlob(track, video);
      if (!blob) throw new Error("captura sem conteúdo (sensor e stream indisponíveis)");

      const captura = await uploadCaptura(sessionId, blob);
      console.info("[captureViaMobi] captura criada", captura);

      await publisher.session.signal({
        data: String(captura.idFoto),
        type: `${Signals.ScreenShot}:${event.data}`,
      });
      await publisher.session.signal({
        data: String(captura.idFoto),
        type: `room:${sessionId}:signedURL`,
      });

      // Fora do caminho crítico do flash — falha aqui não invalida a captura (o widget
      // da tela do perito permite reclassificar).
      classificarCaptura(sessionId, captura.idFoto).catch((error) =>
        console.warn("[captureViaMobi] classificação falhou", error)
      );
    } catch (error: any) {
      console.error("[captureViaMobi] falha na captura", error);
      await publisher.session.signal({
        data: error.message,
        type: `${Signals.ScreenShot}:${event.data}:failed`,
      });
    }
  }

  async function subscribeToCustomEvents(session: Session, publisher: Publisher) {
    session.on("signal:screenshot", async (event: SignalEvent) => {
      console.info(`${event.data} frame has been requested`, event);
      console.log(`isActiveVideo:`, publisher.stream.videoActive);

      if (props.connectionStatus !== ConnectionStatus.CONNECTED) return;

      if (CAPTURE_VIA_MOBI) {
        await captureViaMobi(publisher, event);
        return;
      }

      // Caminho legado (flag desligada): frame do stream em PNG → PUT na PAR.
      const uniqueId = `${publisher.session.sessionId}-${new Date().getTime()}`;
      const payload = { data: event.data, uniqueId, sessionId: publisher.session.sessionId };

      const canvasScreen = await generateCanvasForCapture(publisher);

      canvasScreen.toBlob(async (blob) => {
        try {
          if (blob) {
            await uploadCurrentScreen(payload, blob);
            await publisher.session.signal({
              data: uniqueId,
              type: `${Signals.ScreenShot}:${payload.data}`,
            });
          }
        } catch (error: any) {
          await publisher.session.signal({
            data: error.message,
            type: `${Signals.ScreenShot}:${event.data}`,
          });
        }
      });
    });

    async function finishSession(reason: string) {
      try {
        session.disconnect();
        setSessionStatus(SessionStatus.Closed);
        await completeToken(session.sessionId);
      } catch (error) {
        console.warn(error);
      }

      console.log("Encerrando sessão do segurado", { reason, insuranceId: props.insuranceId, userId: props.userId });
      if (props.insuranceId && props.userId) {
        redirectToCSAT(props.insuranceId!, props.userId!);
        return;
      }
      redirectToMobi(session.sessionId);
    }

    // O perito emite signal:finished ao encerrar a vistoria.
    session.on("signal:finished", (event: SignalEvent) => {
      console.log({ event });
      void finishSession("signal:finished");
    });
  }

  async function generateCanvasForCapture(publisher: Publisher): Promise<HTMLCanvasElement> {
    const canvas = document.createElement("canvas");
    console.log(`publisher.stream.videoActive`, publisher.stream.videoActive);

    const { video: videoFromPublisher } = publisher.videos[publisher.videos.length - 1];
    console.log(publisher.videos);
    console.log(videoFromPublisher)
    const videoFromDocument: HTMLVideoElement = document.getElementById(`local-video--undefined`) as HTMLVideoElement;

    const isSameVideo = await compareVideoHash(videoFromPublisher, videoFromDocument);

    const video = isSameVideo ? videoFromPublisher : videoFromDocument;
    console.log(`HTMLVideoElement:`, isSameVideo);
    const canvasScreen: HTMLCanvasElement = props.captureCurrentScreen(video, canvas);
    return canvasScreen;
  }

  async function compareVideoHash(pubElement: HTMLVideoElement, domElement: HTMLVideoElement) {
    const publisherHash = await generateVideoHash(pubElement);
    const documentHash = await generateVideoHash(domElement);

    return publisherHash === documentHash;
  }

  async function generateVideoHash(videoElement: HTMLVideoElement) {
    console.log(videoElement)
    const blob = await fetch(videoElement.src).then((response) => response.blob());

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function () {
        const buffer = reader.result;

        if (buffer instanceof ArrayBuffer) {
          crypto.subtle
            .digest("SHA-256", buffer)
            .then((hash) => {
              const hashArray = Array.from(new Uint8Array(hash));
              const hashedString = hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
              resolve(hashedString);
            })
            .catch((error) => {
              reject(error);
            });
        }
      };
      reader.readAsArrayBuffer(blob);
    });
  }

  useEffect(() => {
    console.log({ subscribers });
    console.log({ sessionStatus });

    subscribers.length > 0 ? setSessionStatus(SessionStatus.Active) : setSessionStatus(SessionStatus.Awaiting);
  }, [subscribers, sessionStatus]);

  return {
    subscribers,
    sessionStatus,
    setSubscribers,
    subscribeToCustomEvents,
    subscribeToPublisherEvents,
  };
}
